export interface CaptureVideoScreenshotOptions {
  seekSeconds?: number
  timeoutMs?: number
  mimeType?: string
  quality?: number
  maxWidth?: number
}

export interface CaptureFrameFromVideoElementOptions {
  mimeType?: string
  quality?: number
  maxWidth?: number
}

const DEFAULT_TIMEOUT_MS = 8000
const DEFAULT_MAX_WIDTH = 1280
const DEFAULT_SEEK_SECONDS = 1

const raceWithTimeout = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), timeoutMs)
    promise
      .then(value => {
        clearTimeout(timer)
        resolve(value)
      })
      .catch(error => {
        clearTimeout(timer)
        reject(error)
      })
  })
}

const waitForEvent = (target: HTMLVideoElement, event: string, errorEvent = 'error'): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    const onEvent = () => {
      target.removeEventListener(event, onEvent)
      target.removeEventListener(errorEvent, onError)
      resolve()
    }
    const onError = () => {
      target.removeEventListener(event, onEvent)
      target.removeEventListener(errorEvent, onError)
      reject(new Error(`video ${errorEvent}`))
    }
    target.addEventListener(event, onEvent)
    target.addEventListener(errorEvent, onError)
  })
}

const resolveSeekTarget = (duration: number, seekSeconds: number): number => {
  if (!isFinite(duration) || isNaN(duration) || duration <= 0) return 0
  return Math.min(seekSeconds, duration * 0.1)
}

export const captureFrameFromVideoElement = async (
  video: HTMLVideoElement,
  options?: CaptureFrameFromVideoElementOptions,
): Promise<Blob | null> => {
  const maxWidth = options?.maxWidth ?? DEFAULT_MAX_WIDTH
  const mimeType = options?.mimeType ?? 'image/png'

  const width = video.videoWidth
  const height = video.videoHeight
  if (!width || !height) return null

  const scale = Math.min(1, maxWidth / width)
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(width * scale)
  canvas.height = Math.round(height * scale)

  const context = canvas.getContext('2d')
  if (!context) return null
  context.drawImage(video, 0, 0, canvas.width, canvas.height)

  return new Promise<Blob | null>(resolve => {
    canvas.toBlob(result => resolve(result), mimeType, options?.quality)
  })
}

export const captureVideoScreenshot = async (
  file: File,
  options?: CaptureVideoScreenshotOptions,
): Promise<Blob | null> => {
  const timeoutMs = options?.timeoutMs ?? DEFAULT_TIMEOUT_MS
  const seekSeconds = options?.seekSeconds ?? DEFAULT_SEEK_SECONDS

  const video = document.createElement('video')
  video.muted = true
  video.playsInline = true
  const objectUrl = URL.createObjectURL(file)
  video.src = objectUrl

  try {
    await raceWithTimeout(waitForEvent(video, 'loadedmetadata'), timeoutMs)

    video.currentTime = resolveSeekTarget(video.duration, seekSeconds)
    await raceWithTimeout(waitForEvent(video, 'seeked'), timeoutMs)

    return await captureFrameFromVideoElement(video, {
      maxWidth: options?.maxWidth,
      mimeType: options?.mimeType,
      quality: options?.quality,
    })
  } catch (error) {
    console.warn('captureVideoScreenshot: failed to capture screenshot', error)
    return null
  } finally {
    URL.revokeObjectURL(objectUrl)
    video.removeAttribute('src')
    video.load()
  }
}

export const deriveScreenshotFileName = (videoFileName: string): string => {
  const withoutExtension = videoFileName.replace(/\.[^/.]+$/, '')
  return `${withoutExtension}.png`
}
